import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Breadcrumbs from '../components/Breadcrumbs';
import BackButton from '../components/BackButton';

interface HoleScore {
  hole: number;
  par: number;
  score: number;
  putts?: number;
  fairwayHit?: boolean;
  greenInRegulation?: boolean;
  notes?: string;
}

interface Round {
  id: string;
  courseId: string;
  courseName: string;
  date: string;
  score: number;
  par: number;
  holesPlayed: number;
  weather: string;
  notes?: string;
  holeScores: HoleScore[];
  totalPutts: number;
  fairwaysHit: number;
  greensInRegulation: number;
}

export default function RoundDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [round, setRound] = useState<Round | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRoundDetails();
  }, [id]);

  const fetchRoundDetails = async () => {
    try {
      // TODO: Replace with actual API call
      // For now, using mock data based on the round ID
      setTimeout(() => {
        const mockRound: Round = {
          id: id || '1',
          courseId: '1',
          courseName: 'Pine Rivers Golf Club',
          date: '2024-06-15',
          score: 82,
          par: 70,
          holesPlayed: 18,
          weather: 'Sunny',
          notes: 'Great day, improved putting. Driver was working well on the back 9.',
          totalPutts: 32,
          fairwaysHit: 12,
          greensInRegulation: 11,
          holeScores: [
            { hole: 1, par: 4, score: 5, putts: 2, fairwayHit: true, greenInRegulation: false },
            { hole: 2, par: 4, score: 4, putts: 2, fairwayHit: true, greenInRegulation: true },
            { hole: 3, par: 5, score: 6, putts: 3, fairwayHit: false, greenInRegulation: false },
            { hole: 4, par: 4, score: 4, putts: 2, fairwayHit: true, greenInRegulation: true },
            { hole: 5, par: 3, score: 3, putts: 2, fairwayHit: true, greenInRegulation: true },
            { hole: 6, par: 5, score: 5, putts: 2, fairwayHit: true, greenInRegulation: true },
            { hole: 7, par: 4, score: 4, putts: 1, fairwayHit: true, greenInRegulation: true },
            { hole: 8, par: 3, score: 4, putts: 2, fairwayHit: true, greenInRegulation: false },
            { hole: 9, par: 4, score: 5, putts: 2, fairwayHit: false, greenInRegulation: false },
            { hole: 10, par: 3, score: 3, putts: 2, fairwayHit: true, greenInRegulation: true },
            { hole: 11, par: 4, score: 4, putts: 2, fairwayHit: true, greenInRegulation: true },
            { hole: 12, par: 4, score: 3, putts: 1, fairwayHit: true, greenInRegulation: true },
            { hole: 13, par: 4, score: 5, putts: 2, fairwayHit: false, greenInRegulation: false },
            { hole: 14, par: 3, score: 4, putts: 2, fairwayHit: true, greenInRegulation: false },
            { hole: 15, par: 4, score: 4, putts: 2, fairwayHit: true, greenInRegulation: true },
            { hole: 16, par: 4, score: 5, putts: 3, fairwayHit: false, greenInRegulation: false },
            { hole: 17, par: 4, score: 4, putts: 2, fairwayHit: true, greenInRegulation: true },
            { hole: 18, par: 4, score: 5, putts: 2, fairwayHit: true, greenInRegulation: false }
          ]
        };
        setRound(mockRound);
        setLoading(false);
      }, 800);
    } catch (error) {
      console.error('Failed to fetch round details:', error);
      setLoading(false);
    }
  };

  const getScoreColor = (score: number, par: number) => {
    const differential = score - par;
    if (differential <= -2) return 'text-blue-600 dark:text-blue-400'; // Eagle or better
    if (differential === -1) return 'text-emerald-600 dark:text-emerald-400'; // Birdie
    if (differential === 0) return 'text-golf-600 dark:text-golf-400'; // Par
    if (differential === 1) return 'text-orange-600 dark:text-orange-400'; // Bogey
    if (differential === 2) return 'text-red-600 dark:text-red-400'; // Double bogey
    return 'text-red-800 dark:text-red-600'; // Triple bogey or worse
  };

  const getScoreLabel = (score: number, par: number) => {
    const differential = score - par;
    if (differential <= -3) return 'Albatross';
    if (differential === -2) return 'Eagle';
    if (differential === -1) return 'Birdie';
    if (differential === 0) return 'Par';
    if (differential === 1) return 'Bogey';
    if (differential === 2) return 'Double';
    if (differential === 3) return 'Triple';
    return `+${differential}`;
  };

  const handleDelete = async () => {
    if (confirm('Are you sure you want to delete this round? This action cannot be undone.')) {
      try {
        // TODO: Implement actual delete API call
        console.log('Deleting round:', id);
        navigate('/rounds');
      } catch (error) {
        console.error('Failed to delete round:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-golf-50 to-emerald-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-golf-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading round details...</p>
        </div>
      </div>
    );
  }

  if (!round) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-golf-50 to-emerald-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">Round Not Found</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            The round you're looking for doesn't exist or has been deleted.
          </p>
          <Link 
            to="/rounds"
            className="bg-gradient-to-r from-golf-500 to-emerald-500 hover:from-golf-600 hover:to-emerald-600 text-white px-8 py-3 rounded-lg font-medium transition-all duration-300 inline-block shadow-lg hover:shadow-xl"
          >
            Back to Rounds
          </Link>
        </div>
      </div>
    );
  }

  const front9 = round.holeScores.slice(0, 9);
  const back9 = round.holeScores.slice(9, 18);
  const front9Score = front9.reduce((sum, hole) => sum + hole.score, 0);
  const back9Score = back9.reduce((sum, hole) => sum + hole.score, 0);
  const front9Par = front9.reduce((sum, hole) => sum + hole.par, 0);
  const back9Par = back9.reduce((sum, hole) => sum + hole.par, 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-emerald-50 dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumbs */}
          <Breadcrumbs 
            items={[
              { label: 'Rounds', href: '/rounds' },
              { label: round.courseName }
            ]}
          />
          
          {/* Header */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <BackButton to="/rounds" label="Back to Rounds" className="mb-2" />
              <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-200">{round.courseName}</h1>
              <p className="text-gray-600 dark:text-gray-400">
                {new Date(round.date).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })} • {round.weather}
              </p>
            </div>
            <div className="flex gap-2">
              <Link
                to={`/rounds/${round.id}/edit`}
                className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Edit Round
              </Link>
              <button
                onClick={handleDelete}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          </div>

          {/* Round Summary */}
          <div className="bg-white dark:bg-gray-800/50 backdrop-blur rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4">Round Summary</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Score</p>
                <p className="text-3xl font-bold text-golf-600 dark:text-golf-400">{round.score}</p>
                <p className="text-xs text-gray-500">
                  {round.score - round.par > 0 ? '+' : ''}{round.score - round.par}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Putts</p>
                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{round.totalPutts}</p>
                <p className="text-xs text-gray-500">
                  {(round.totalPutts / round.holesPlayed).toFixed(1)} avg
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Fairways Hit</p>
                <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                  {round.fairwaysHit}
                </p>
                <p className="text-xs text-gray-500">
                  {Math.round((round.fairwaysHit / round.holesPlayed) * 100)}%
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">GIR</p>
                <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                  {round.greensInRegulation}
                </p>
                <p className="text-xs text-gray-500">
                  {Math.round((round.greensInRegulation / round.holesPlayed) * 100)}%
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Holes Played</p>
                <p className="text-3xl font-bold text-gray-600 dark:text-gray-400">{round.holesPlayed}</p>
                <p className="text-xs text-gray-500">of 18</p>
              </div>
            </div>
          </div>

          {/* Detailed Scorecard */}
          <div className="bg-white dark:bg-gray-800/50 backdrop-blur rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4">Detailed Scorecard</h2>
            
            {/* Front 9 */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3">Front 9</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b dark:border-gray-700">
                      <th className="text-left py-2 px-2">Hole</th>
                      <th className="text-center py-2 px-2">Par</th>
                      <th className="text-center py-2 px-2">Score</th>
                      <th className="text-center py-2 px-2">+/-</th>
                      <th className="text-center py-2 px-2">Putts</th>
                      <th className="text-center py-2 px-2">FIR</th>
                      <th className="text-center py-2 px-2">GIR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {front9.map((hole) => (
                      <tr key={hole.hole} className="border-b dark:border-gray-700/50">
                        <td className="py-2 px-2 font-medium">{hole.hole}</td>
                        <td className="py-2 px-2 text-center text-gray-600 dark:text-gray-400">{hole.par}</td>
                        <td className={`py-2 px-2 text-center font-bold ${getScoreColor(hole.score, hole.par)}`}>
                          {hole.score}
                        </td>
                        <td className={`py-2 px-2 text-center text-xs ${getScoreColor(hole.score, hole.par)}`}>
                          {getScoreLabel(hole.score, hole.par)}
                        </td>
                        <td className="py-2 px-2 text-center">{hole.putts}</td>
                        <td className="py-2 px-2 text-center">
                          {hole.fairwayHit ? '✅' : '❌'}
                        </td>
                        <td className="py-2 px-2 text-center">
                          {hole.greenInRegulation ? '✅' : '❌'}
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-gray-300 dark:border-gray-600 font-bold">
                      <td className="py-2 px-2">OUT</td>
                      <td className="py-2 px-2 text-center">{front9Par}</td>
                      <td className="py-2 px-2 text-center">{front9Score}</td>
                      <td className="py-2 px-2 text-center">
                        {front9Score - front9Par > 0 ? '+' : ''}{front9Score - front9Par}
                      </td>
                      <td className="py-2 px-2 text-center">
                        {front9.reduce((sum, h) => sum + (h.putts || 0), 0)}
                      </td>
                      <td className="py-2 px-2 text-center">
                        {front9.filter(h => h.fairwayHit).length}/9
                      </td>
                      <td className="py-2 px-2 text-center">
                        {front9.filter(h => h.greenInRegulation).length}/9
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Back 9 */}
            {back9.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3">Back 9</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b dark:border-gray-700">
                        <th className="text-left py-2 px-2">Hole</th>
                        <th className="text-center py-2 px-2">Par</th>
                        <th className="text-center py-2 px-2">Score</th>
                        <th className="text-center py-2 px-2">+/-</th>
                        <th className="text-center py-2 px-2">Putts</th>
                        <th className="text-center py-2 px-2">FIR</th>
                        <th className="text-center py-2 px-2">GIR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {back9.map((hole) => (
                        <tr key={hole.hole} className="border-b dark:border-gray-700/50">
                          <td className="py-2 px-2 font-medium">{hole.hole}</td>
                          <td className="py-2 px-2 text-center text-gray-600 dark:text-gray-400">{hole.par}</td>
                          <td className={`py-2 px-2 text-center font-bold ${getScoreColor(hole.score, hole.par)}`}>
                            {hole.score}
                          </td>
                          <td className={`py-2 px-2 text-center text-xs ${getScoreColor(hole.score, hole.par)}`}>
                            {getScoreLabel(hole.score, hole.par)}
                          </td>
                          <td className="py-2 px-2 text-center">{hole.putts}</td>
                          <td className="py-2 px-2 text-center">
                            {hole.fairwayHit ? '✅' : '❌'}
                          </td>
                          <td className="py-2 px-2 text-center">
                            {hole.greenInRegulation ? '✅' : '❌'}
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t-2 border-gray-300 dark:border-gray-600 font-bold">
                        <td className="py-2 px-2">IN</td>
                        <td className="py-2 px-2 text-center">{back9Par}</td>
                        <td className="py-2 px-2 text-center">{back9Score}</td>
                        <td className="py-2 px-2 text-center">
                          {back9Score - back9Par > 0 ? '+' : ''}{back9Score - back9Par}
                        </td>
                        <td className="py-2 px-2 text-center">
                          {back9.reduce((sum, h) => sum + (h.putts || 0), 0)}
                        </td>
                        <td className="py-2 px-2 text-center">
                          {back9.filter(h => h.fairwayHit).length}/9
                        </td>
                        <td className="py-2 px-2 text-center">
                          {back9.filter(h => h.greenInRegulation).length}/9
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Total */}
            <div className="border-t-2 border-gray-400 dark:border-gray-500 pt-4">
              <div className="grid grid-cols-7 gap-4 text-center font-bold text-lg">
                <div>TOTAL</div>
                <div>{round.par}</div>
                <div className="text-golf-600 dark:text-golf-400">{round.score}</div>
                <div>
                  {round.score - round.par > 0 ? '+' : ''}{round.score - round.par}
                </div>
                <div>{round.totalPutts}</div>
                <div>{round.fairwaysHit}/18</div>
                <div>{round.greensInRegulation}/18</div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {round.notes && (
            <div className="bg-white dark:bg-gray-800/50 backdrop-blur rounded-xl shadow-lg p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4">Round Notes</h2>
              <p className="text-gray-700 dark:text-gray-300 italic">"{round.notes}"</p>
            </div>
          )}
          
          {/* Bottom Navigation */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <BackButton to="/dashboard" label="Back to Dashboard" />
            <div className="flex gap-3">
              <Link
                to="/rounds"
                className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 px-6 py-2 rounded-lg font-medium transition-colors"
              >
                View All Rounds
              </Link>
              <Link
                to="/rounds/new"
                className="bg-gradient-to-r from-golf-500 to-emerald-500 hover:from-golf-600 hover:to-emerald-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                New Round
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}